// frontend/src/lib/groups/migrateLegacyGroups.js

/**
 * Idempotent, deterministic migration of legacy `groupId` (flat sibling marker)
 * into real `group` container elements with `parentId` hierarchy.
 *
 * Z-ORDER CONTRACT:
 *   - WITHIN a group: child order is preserved EXACTLY. This matters for
 *     mask/overlay/text-image compositions where stacking determines
 *     compositing. Test-pinned.
 *   - ACROSS groups: a non-member element that appeared BETWEEN group
 *     members in the legacy flat array (e.g. `[a(g), unrelated, b(g)]`)
 *     CANNOT be preserved in the hierarchical model — the new structure
 *     requires all group children to be contiguous after the group container.
 *     `unrelated` is repositioned AFTER the full group block. This is a
 *     known, documented side-effect, accepted by the spec. Test-pinned.
 *
 * Properties:
 *   - Pure: never mutates the input array or any element object. Returns a new array.
 *   - Failure-tolerant: non-array input returns []; malformed groupId (empty,
 *     non-string) is ignored; pre-existing element with the synthesized
 *     `group_legacy_*` id that is NOT of type 'group' aborts that bucket
 *     (members remain unmigrated rather than silently joining a corrupt element).
 *   - Pre-existing real group with the synthesized id (e.g. from a prior
 *     migration that left a `groupId`-tagged element behind): new bucket
 *     members are reparented into that existing group. The existing group's
 *     `name`/`visible`/`locked` is preserved as-is; the migration does NOT
 *     overwrite container metadata. This is the idempotent merge path.
 *   - Re-running on already-migrated data: no-op (elements with parentId are
 *     skipped; deterministic group id prevents duplicate creation).
 *   - Visually stable: preserves x/y/w/h, rotation exactly.
 *   - Mixed legacy + new-model data: an element with `parentId` set is left
 *     alone even if `groupId` is also present (new field wins).
 *
 * Returns a NEW array; never mutates input.
 */
export function migrateLegacyGroups(pageElements) {
  if (!Array.isArray(pageElements)) return [];

  // Pass 1: bucket legacy-grouped elements by their groupId.
  const byLegacyId = new Map();
  for (const el of pageElements) {
    if (el.parentId) continue;                            // already in new model
    if (!el.groupId || typeof el.groupId !== 'string') continue;
    if (!byLegacyId.has(el.groupId)) byLegacyId.set(el.groupId, []);
    byLegacyId.get(el.groupId).push(el);
  }
  if (byLegacyId.size === 0) return pageElements;

  // Pass 2: synthesize a group container per bucket. Deterministic id so
  // a second migration pass doesn't create duplicates.
  const existingIds = new Set(pageElements.map((e) => e.id));
  const out = [];
  const visited = new Set();
  let groupCounter = 1;

  for (const el of pageElements) {
    if (visited.has(el.id)) continue;
    const legacyId = el.groupId;
    const bucket = legacyId ? byLegacyId.get(legacyId) : null;
    if (bucket && bucket.length >= 1) {
      const newGroupId = `group_legacy_${legacyId}`;

      // Pre-existing element with this id?
      //   - type === 'group': benign — this is the idempotent merge path
      //     (e.g. a prior migration left an element with legacy groupId
      //     pointing at a group that already exists). New bucket members
      //     get reparented into the existing group container; its metadata
      //     is preserved. Falls through to the `existingIds.has(newGroupId)`
      //     guard below which skips re-creation.
      //   - type !== 'group': unsafe — silent merge would corrupt unrelated
      //     data. Skip this bucket entirely; members keep their legacy
      //     groupId and remain unmigrated (caller can resolve the id
      //     collision and retry).
      const preExisting = pageElements.find((e) => e.id === newGroupId);
      if (preExisting && preExisting.type !== 'group') {
        out.push(el);
        continue;
      }

      // Insert group container once (only on first encounter of bucket).
      if (!existingIds.has(newGroupId)) {
        out.push({
          id: newGroupId,
          type: 'group',
          name: `Group ${groupCounter++}`,
          parentId: null,
          visible: bucket.every((m) => m.visible !== false),
          locked: bucket.some((m) => m.locked === true),
          derivedBounds: null,
        });
        existingIds.add(newGroupId);
      }
      // Add each member with parentId set, groupId dropped.
      for (const m of bucket) {
        visited.add(m.id);
        const { groupId: _drop, ...rest } = m;
        out.push({ ...rest, parentId: newGroupId });
      }
      byLegacyId.delete(legacyId);
    } else {
      out.push(el);
    }
  }
  return out;
}
